import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { detectTenant } from "@/config/tenants";
import { trackLogin, trackLogout, identifyUser } from "@/lib/analytics";

const AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ── Cache keys ──────────────────────────────────────────────────────────────
const TOKEN_KEY = "token";
const USER_KEY  = "auth_user";   // cached user to render instantly on reload

/**
 * Résout le tenant_backend_id (super-admin only).
 * DOIT être awaité avant de laisser les appels API partir, sinon
 * les requêtes partent avec l'ancien tenant_backend_id dans le header X-Tenant-Id.
 */
async function resolveTenantBackendId(token) {
  const tenant = detectTenant();
  if (!tenant.backendSlug) return;
  try {
    const res = await axios.get(`${BACKEND_URL}/api/admin/tenants`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const match = res.data.find((t) => t.slug === tenant.backendSlug);
    if (match) {
      localStorage.setItem("tenant_backend_id", match.id);
    } else {
      // Slug non trouvé dans les tenants — nettoyer pour éviter données croisées
      localStorage.removeItem("tenant_backend_id");
    }
  } catch {
    // En cas d'erreur réseau, on ne touche pas au localStorage
  }
}

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(() => {
    // Instant restore from cache — no network call on render
    try { return JSON.parse(localStorage.getItem(USER_KEY)) || null; }
    catch { return null; }
  });
  const [loading, setLoading] = useState(true);   // true par défaut — on attend la résolution complète

  // Verify token silently in background (only on mount with a token)
  useEffect(() => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) { setLoading(false); return; }

    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(res.data);
        localStorage.setItem(USER_KEY, JSON.stringify(res.data));
        // Super-admin: AWAIT la résolution du tenant AVANT de terminer le loading
        // Ceci garantit que les pages qui chargent après voient le bon tenant_backend_id
        if (res.data.is_super_admin) {
          await resolveTenantBackendId(token);
        }
      } catch {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        localStorage.removeItem("tenant_backend_id");
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const logout = useCallback(() => {
    trackLogout();
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem("tenant_backend_id");
    setUser(null);
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(userData));
    // Super-admin: résoudre le tenant_backend_id AVANT de retourner (bloquant)
    if (userData.is_super_admin) await resolveTenantBackendId(token);
    setUser(userData);
    trackLogin("email");
    const tenant = detectTenant();
    identifyUser({ role: userData.role, tenantSlug: tenant.backendSlug || "unknown" });
    return userData;
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}